import { fg } from '@atlaskit/platform-feature-flags/fg';

import { type ClientId } from '../../client/bridge-api-client-types';
import { emitter } from '../../events/bridge-events-emitter';
import {
	isRPCRequest,
	isRPCResponse,
	type RPCMessage,
	type RPCRequest,
} from '../../rpc/bridge-api-rpc-message';
import { isTransportEnvelope, type TransportEnvelope } from '../bridge-api-transport-envelope';
import { type Transport } from '../bridge-api-transport-types';

import { findTopmostSameOriginWindow, isSameOrigin } from './browser-apis';
import { getWebviewAPI, isWebviewContext, sendToMainProcess } from './desktop-apis';
import { findCrossOriginTarget, type ResolvedCrossOriginTarget } from './target-resolver';

/* eslint-disable no-console */

export type BrowserTargetResolver = (args: {
	receiverId: ClientId;
	source: string;
}) => Window | undefined;

export type BrowserTransportOptions = {
	/**
	 * Resolve a browser window target for a receiver before falling back to the default browser
	 * target resolution strategy.
	 */
	resolveTarget?: BrowserTargetResolver;

	/**
	 * Exact origins permitted for targets whose origin cannot be discovered by the default resolver.
	 * Values must come from trusted host configuration, never from a URL or message payload.
	 */
	allowedOrigins?: readonly string[];
};

const MESSAGE_ORIGIN_VALIDATION_GATE = 'rovo_bridge_validate_message_origin';

const normaliseOrigins = (origins: readonly string[] | undefined): ReadonlySet<string> => {
	const result = new Set<string>();

	for (const value of origins ?? []) {
		try {
			const origin = new URL(value).origin;
			if (origin !== 'null') {
				result.add(origin);
			}
		} catch {
			// An invalid value must not accidentally widen the trust boundary.
		}
	}

	return result;
};

/**
 * Narrows a {@link MessageEvent.source} to a window.
 *
 * @description A window proxy exposes `window` as a self-reference even across an origin boundary,
 * which message ports and service workers do not.
 */
const isWindowSource = (source: MessageEventSource | null): source is Window => {
	try {
		return Boolean(source) && (source as Window).window === source;
	} catch {
		return false;
	}
};

/**
 * A browser-compatible transport protocol, used for communicating with cross-origin targets.
 *
 * Leverages {@link window.postMessage} to communicate with such targets, with narrowing to a specific `iframe`
 * window achieved by utilising the {@link RPCMessage.params.receiverId} as an expected data attribute value on
 * an <iframe> which elects itself to be a valid communication target.
 *
 * I.e., a cross-origin <iframe> which is to be communicated with using a `receiverId` of `my-app` must declare
 * a data attribute on its <iframe> markup with `data-rovo-receiver-id="my-app"` for {@link BrowserTransport} to work.
 *
 * Additionally, supports communication in a desktop context, if rendered in a web view. All communications take place
 * between a browser web view <-> main desktop process. Relies upon existence of a webview API on the renderer window.
 */
export class BrowserTransport implements Transport {
	/**
	 * A mapping of receiver IDs to known targets.
	 *
	 * Communication will ultimately take place with this target, via the {@link HTMLIFrameElement.contentWindow} property.
	 */
	private targets = new Map<ClientId, ResolvedCrossOriginTarget>();

	private readonly allowedOrigins: ReadonlySet<string>;

	// New requests can use an authenticated peer origin; replies retain their own request's destination.
	private authenticatedOrigins = new WeakMap<Window, string>();
	private replyTargets = new WeakMap<RPCRequest, { target: Window; origin: string }>();

	/**
	 * Handles incoming messages received via {@link window.postMessage}.
	 */
	private handler: ((data: TransportEnvelope) => void) | undefined = undefined;

	/**
	 * Cleanup handler registered for incoming messages
	 */
	private cleanup: VoidFunction | undefined = undefined;

	constructor(
		public source: string,
		private readonly options: BrowserTransportOptions = {},
	) {
		this.allowedOrigins = normaliseOrigins(options.allowedOrigins);

		emitter().emit({
			type: 'transport-ready',
			transportType: this.source,
		});
	}

	/**
	 * Check to see if this message can be sent to its intended cross-origin target currently.
	 *
	 * @description Determined by availability of the target window in the DOM.
	 * @description If this method does not yield true, the message will be buffered & tried again later.
	 *
	 * @param message intended RPC message for the receiver
	 * @returns true if the transport target is queryable via DOM, otherwise false
	 */
	canSend(message: RPCMessage): boolean {
		// trying to communicate with desktop process
		if (isWebviewContext()) {
			return true;
		}

		// passthrough for same-origin communication
		if (isSameOrigin(window)) {
			return true;
		}

		// otherwise find the cross-origin target & communicate with it
		const target = this.findTarget(message.params.receiverId);
		return Boolean(target);
	}

	/**
	 * Sends a cross-origin message using {@link window.postMessage}.
	 * @param message intended RPC message for the receiver
	 * @param replyTo original accepted request, required for browser replies while origin validation is enabled
	 * @returns void
	 */
	send(message: RPCMessage, replyTo?: RPCRequest): void {
		// in a webview context and trying to communicate with main process
		if (isWebviewContext()) {
			sendToMainProcess(message, this.source);
			return;
		}

		const validateOrigin = fg(MESSAGE_ORIGIN_VALIDATION_GATE);
		const target =
			validateOrigin && isRPCResponse(message)
				? replyTo && this.replyTargets.get(replyTo)
				: this.findTarget(message.params.receiverId);
		if (!target) {
			emitter().emit({
				type: 'transport-error',
				transportType: this.source,
				direction: 'outbound',
				messageId: message.id,
				error: {
					message: `Transport target not found for receiver: ${message.params.receiverId}`,
					code: 'TARGET_NOT_FOUND',
				},
			});
			return;
		}

		const envelope: TransportEnvelope = {
			version: 1,
			source: this.source,
			message,
		};

		emitter().emit({
			type: 'transport-message-sent',
			transportType: this.source,
			direction: 'outbound',
			messageId: message.id,
			message: { ...envelope },
		});

		target.target.postMessage(envelope, validateOrigin ? this.outboundOrigin(target) : '*');
		if (validateOrigin && replyTo) {
			this.replyTargets.delete(replyTo);
		}
		return;
	}

	/**
	 * Handles incoming events from other bridge API clients.
	 * @param handler responds to a given RPC message from a remote client.
	 */
	onMessage(handler: (data: RPCMessage) => void): void {
		// do not allow overriding of handlers.
		if (this.handler) {
			return;
		}

		// stable reference so we can destroy it later
		this.handler = (envelope: TransportEnvelope) => this.handleEnvelope(envelope, handler);

		// listen for messsages from webview context
		if (isWebviewContext()) {
			const webviewAPI = getWebviewAPI();
			if (!webviewAPI) {
				return;
			}

			this.cleanup = webviewAPI.onMessageFromMain((envelope) => this.handler?.(envelope));
		}
		// listen for messages from web context
		else {
			// Find topmost same-origin window to listen on
			const targetWindow = findTopmostSameOriginWindow(window);

			// web-compatible handler
			const handler = (event: MessageEvent<TransportEnvelope>) => {
				if (!fg(MESSAGE_ORIGIN_VALIDATION_GATE)) {
					this.handler?.(event.data);
					return;
				}

				if (this.isTrustedSender(event, targetWindow)) {
					this.recordAuthenticatedPeer(event);
					this.handler?.(event.data);
				}
			};
			targetWindow.addEventListener('message', handler);
			this.cleanup = () => targetWindow.removeEventListener('message', handler);
		}
	}

	/**
	 * Tears down a specific client by removing its target reference.
	 * @description This is important to release iframe / window references fully.
	 */
	teardown(clientId: ClientId): void {
		this.targets.delete(clientId);
	}

	/**
	 * Clears all relevant state.
	 */
	destroy(): void {
		// clean up handler
		if (this.cleanup) {
			this.cleanup();
			this.cleanup = undefined;
		}

		// clean up handler reference
		if (this.handler) {
			this.handler = undefined;
		}

		// clear all target references
		this.targets.clear();
		this.authenticatedOrigins = new WeakMap();
		this.replyTargets = new WeakMap();

		// emit transport destroyed event
		emitter().emit({
			type: 'transport-destroyed',
			transportType: this.source,
		});
	}

	/**
	 * Find a target for the given receiver in one of three ways:
	 * - Using the existing mapping of known targets ({@link #targets})
	 * - Using a DOM query for a cross-origin `iframe`, based on a specific DOM selector contract
	 * - Using {@link window.top} if communicating cross-origin from inside of a frame
	 *
	 * @description If a target is find with one of the latter strategies, it is added to {@link #targets} for subsequent reads.
	 *
	 * @param receiverId intended target of the current message
	 * @returns target if found, otherwise undefined
	 */
	private findTarget(receiverId: ClientId): ResolvedCrossOriginTarget | undefined {
		// if existing, return
		const target = this.targets.get(receiverId);
		if (target) {
			return target;
		}

		// Find target using a provided resolution strategy
		const resolvedTarget = this.options.resolveTarget?.({
			receiverId,
			source: this.source,
		});
		if (resolvedTarget) {
			const resolved: ResolvedCrossOriginTarget = { target: resolvedTarget };
			this.targets.set(receiverId, resolved);
			return resolved;
		}

		// Find target using resolution strategies
		const browserTarget = findCrossOriginTarget(receiverId);
		if (browserTarget) {
			this.targets.set(receiverId, browserTarget);
			return browserTarget;
		}

		console.warn(`transport target not found for message with intended recipient: ${receiverId}`);
		return undefined;
	}

	private recordAuthenticatedPeer(event: MessageEvent<TransportEnvelope>): void {
		if (!isWindowSource(event.source)) {
			return;
		}

		this.authenticatedOrigins.set(event.source, event.origin);
		if (isRPCRequest(event.data.message)) {
			this.replyTargets.set(event.data.message, { target: event.source, origin: event.origin });
		}
	}

	private outboundOrigin(target: ResolvedCrossOriginTarget): string {
		return target.origin ?? this.authenticatedOrigins.get(target.target) ?? '*';
	}

	/**
	 * Whether the envelope's self-declared source agrees with the origin the browser stamped on the event.
	 */
	private hasAttestedOrigin(event: MessageEvent<TransportEnvelope>): boolean {
		return isTransportEnvelope(event.data) && event.data.source === event.origin;
	}

	/**
	 * Whether an inbound message satisfies the transport's origin and peer checks.
	 *
	 * Same-origin messages are accepted after their envelope source is checked. Cross-origin messages
	 * must also match a resolved peer using two fields supplied by the browser:
	 * - {@link MessageEvent.source}: a live reference to the actual sending window. Compared by identity
	 *   against the window resolved by {@link findTarget}, so another window cannot claim its identity.
	 * - {@link MessageEvent.origin}: the sender's origin when the message was sent. Matched against
	 *   the peer origin we derived ourselves (an iframe `src` or ancestor origin) or, only when unknown, an origin in
	 *   {@link BrowserTransportOptions.allowedOrigins}.
	 *
	 * @description Everything inside {@link MessageEvent.data} is attacker-controlled and treated only as
	 * a claim: the self-declared envelope `source` and the `senderId` / `receiverId`. `senderId` is used
	 * purely as a lookup key whose result is then re-checked against the unforgeable fields above, and
	 * {@link hasAttestedOrigin} ties the self-declared `source` back to the browser-stamped origin.
	 */
	private isTrustedSender(event: MessageEvent<TransportEnvelope>, targetWindow: Window): boolean {
		if (!this.hasAttestedOrigin(event)) {
			return false;
		}

		if (event.origin === targetWindow.location.origin) {
			return true;
		}

		const senderId = event.data.message.params?.senderId;
		if (typeof senderId !== 'string') {
			return false;
		}

		const sender = this.findTarget(senderId);
		const trustedOrigin =
			sender?.origin !== undefined
				? sender.origin === event.origin
				: this.allowedOrigins.has(event.origin);

		return Boolean(event.source && sender && event.source === sender.target && trustedOrigin);
	}

	/**
	 * Handles a transport envelope received from a remote source.
	 * @param envelope the transport envelope containing the RPC message
	 * @param handler the handler to invoke for the RPC message contained in the envelope
	 * @returns void
	 */
	private handleEnvelope(envelope: TransportEnvelope, handler: (data: RPCMessage) => void) {
		// always validate first - if invalid, ignore
		if (!isTransportEnvelope(envelope)) {
			return;
		}

		// ensure message is not from the current window to itself
		if (envelope.source === this.source) {
			return;
		}

		emitter().emit({
			type: 'transport-message-received',
			transportType: this.source,
			direction: 'inbound',
			messageId: envelope.message.id,
			transportData: {
				source: envelope.source,
				version: envelope.version,
				message: envelope.message,
			},
		});

		handler(envelope.message);
	}
}
